import { useEffect, useCallback, useRef } from 'react';
import keyBy from 'lodash/keyBy';
import {
	fetchWorkflowIssues,
	Unchanged,
	type WorkflowIssuesData,
	type Issue,
	type WorkflowOld,
} from '@atlassian/jira-business-board-workflow-issues';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { useFlagService } from '@atlassian/jira-flags';
import { useWorkflowIssues } from '@atlassian/jira-router-resources-business-board-workflow-issues';
import { useBoardStoreActions, useBoardStoreState } from '../../services/board-store';
import { useActiveWorkflow } from '../active-workflow-old';
import messages from './messages';

export const remapSubtasks = (workflow: WorkflowOld, existingIssues: Issue[]): WorkflowOld => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const subtasksMap = new Map<number, any>();
	existingIssues.forEach((issue) => {
		// issues don't have a subtask property
		// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
		const subtasks = (issue as any).subtasks;
		if (subtasks) {
			subtasksMap.set(issue.id, subtasks);
		}
	});

	const statusesDictionary = keyBy(workflow.columns, 'statusId');

	return {
		...workflow,
		issues: workflow.issues.map((issue) => ({
			...issue,
			untranslatedStatusName: statusesDictionary[issue.statusId]?.untranslatedName,
			subtasks: subtasksMap.get(issue.id),
		})),
	};
};

const getActiveWorkflow = (workflows: WorkflowOld[], activeWorkflowId: string): WorkflowOld => {
	const activeWorkflow =
		workflows.find((workflow) => workflow.workflowId === activeWorkflowId) ?? workflows[0];

	return activeWorkflow;
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useInitializeBoard = () => {
	const { data, loading, error } = useWorkflowIssues();
	const hasInitialized = useRef(false);
	const { setFetchResponse } = useBoardStoreActions();
	const { showFlag } = useFlagService();
	const [activeWorkflowId] = useActiveWorkflow();

	useEffect(() => {
		if (!hasInitialized.current) {
			if (data) {
				hasInitialized.current = true;

				const activeWorkflowData = getActiveWorkflow(data.workflows, activeWorkflowId);

				if (!activeWorkflowData) {
					return;
				}

				setFetchResponse({
					activeWorkflow: remapSubtasks(activeWorkflowData, []),
					workflows: data.workflows,
					workflowIssuesLastChangedTime: data.lastChangedTime || undefined,
				});
			} else if (error) {
				hasInitialized.current = true;
				showFlag({
					type: 'error',
					title: messages.boardInitErrorTitle,
					description: messages.boardInitErrorMessage,
				});
			}
		}
	}, [activeWorkflowId, data, error, setFetchResponse, showFlag]);

	return { loading, error };
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useRefetchBoardData = () => {
	const project = useProject();
	const { issues: currentIssues, workflowIssuesLastChangedTime } = useBoardStoreState();
	const { setFetchResponse } = useBoardStoreActions();
	const [activeWorkflowId] = useActiveWorkflow();

	return useCallback(async () => {
		try {
			const response: WorkflowIssuesData = await fetchWorkflowIssues(
				project.key,
				workflowIssuesLastChangedTime,
			);

			const activeWorkflowData = getActiveWorkflow(response.workflows, activeWorkflowId);

			if (!activeWorkflowData) {
				return;
			}

			setFetchResponse({
				activeWorkflow: remapSubtasks(activeWorkflowData, currentIssues),
				workflows: response.workflows,
				workflowIssuesLastChangedTime: response.lastChangedTime || undefined,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			if (error instanceof Unchanged) {
				// do nothing when data has not changed
				return;
			}

			fireErrorAnalytics({
				meta: {
					id: 'refetchBoardData',
					packageName: 'jiraWorkManagementBoard',
					teamName: 'wanjel',
				},
				attributes: {
					message: 'Failed to refetch workflow issues data',
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
		}
	}, [activeWorkflowId, currentIssues, project, setFetchResponse, workflowIssuesLastChangedTime]);
};
